/**
 * socialSignIn.js — B-53: "Continue with Apple" / "Continue with Google".
 *
 * Every route ends the same way: we get a provider-signed ID token on the
 * device and hand it to POST /api/auth/social, which verifies it. The
 * client never decides who someone is.
 *
 *   Web     Apple: Apple's own JS (appleid.auth.js), popup mode.
 *           Google: Google Identity Services' rendered button (see
 *           components/SocialSignIn.jsx) — GIS is the only supported way to
 *           get a Google ID token in a browser.
 *   iOS     Apple: the native sheet; Google: Google Sign-In — both through
 *           @capgo/capacitor-social-login (one maintained plugin for both,
 *           Capacitor 8 compatible).
 *   Android Google only (Credential Manager, same plugin). Apple has no
 *           Android SDK; its web-redirect flow is not part of B-53.
 *
 * Where the buttons may appear (server switch first, then the host):
 *   Google web — only on https://app.marakin.co.uk and http://localhost:3000,
 *     the JavaScript origins registered on the web client. On any other host
 *     (the railway.app one) Google would refuse, so no button.
 *   Apple web — only on the two hosts whose /api/auth/apple/callback is a
 *     registered return URL on the Services ID.
 *   iOS — both or neither (App Store rule 4.8; the server's iOS Google flag
 *     already requires iOS Apple).
 *
 * Nonce (security review): issued by the SERVER per attempt (POST
 * /auth/social/nonce, single use, 30 minutes); the SDK gets sha256(raw), the
 * server gets raw back, checks the token carries its hash and uses it up, so
 * a captured token can't be replayed.
 */
import axios from 'axios';
import { Capacitor } from '@capacitor/core';

export const GOOGLE_WEB_ORIGINS = ['https://app.marakin.co.uk', 'http://localhost:3000'];
export const APPLE_WEB_ORIGINS = ['https://app.marakin.co.uk', 'https://marakin-app-production.up.railway.app'];
const APPLE_JS = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_GB/appleid.auth.js';
const GOOGLE_GIS = 'https://accounts.google.com/gsi/client';
// The app's bundle id (capacitor.config.ts appId). On iOS the plugin only
// uses it as a label; the identity token's audience is the bundle id.
const IOS_BUNDLE_ID = 'com.marakin.app';

const OFF = { apple: { web: false, ios: false, android: false }, google: { web: false, ios: false, android: false } };

let configPromise = null;
export function getSocialConfig() {
  if (!configPromise) {
    configPromise = axios.get('/auth/social/config')
      .then(r => r.data || OFF)
      .catch(() => { configPromise = null; return OFF; }); // feature off on error; try again next time
  }
  return configPromise;
}

export const runtimePlatform = () => Capacitor.getPlatform(); // 'web' | 'ios' | 'android'

// Which buttons this device should show, given the server's config.
export function availableProviders(cfg) {
  const p = runtimePlatform();
  if (!cfg) return { apple: false, google: false };
  if (p === 'ios') {
    const apple = !!cfg.apple?.ios;
    return { apple, google: apple && !!cfg.google?.ios };
  }
  if (p === 'android') return { apple: false, google: !!cfg.google?.android };
  const origin = window.location.origin;
  return {
    apple: !!cfg.apple?.web && !!cfg.apple?.servicesId && APPLE_WEB_ORIGINS.includes(origin),
    google: !!cfg.google?.web && !!cfg.google?.webClientId && GOOGLE_WEB_ORIGINS.includes(origin),
  };
}

// ── Nonce ───────────────────────────────────────────────────────────────────
const toHex = (bytes) => Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');

// Small SHA-256 for the rare WebView without crypto.subtle (it needs a
// secure context). Same output as the server's crypto.createHash('sha256').
function sha256Fallback(str) {
  const K = [0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2];
  const bytes = new TextEncoder().encode(str);
  const len = bytes.length;
  const total = Math.ceil((len + 9) / 64) * 64;
  const m = new Uint8Array(total);
  m.set(bytes);
  m[len] = 0x80;
  const bits = len * 8;
  const view = new DataView(m.buffer);
  view.setUint32(total - 8, Math.floor(bits / 0x100000000));
  view.setUint32(total - 4, bits >>> 0);
  const H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const W = new Uint32Array(64);
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) W[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(W[i - 15], 7) ^ rotr(W[i - 15], 18) ^ (W[i - 15] >>> 3);
      const s1 = rotr(W[i - 2], 17) ^ rotr(W[i - 2], 19) ^ (W[i - 2] >>> 10);
      W[i] = (W[i - 16] + s0 + W[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = H;
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + W[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0] + a) >>> 0; H[1] = (H[1] + b) >>> 0; H[2] = (H[2] + c) >>> 0; H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0; H[5] = (H[5] + f) >>> 0; H[6] = (H[6] + g) >>> 0; H[7] = (H[7] + h) >>> 0;
  }
  return H.map(x => x.toString(16).padStart(8, '0')).join('');
}

async function sha256hex(str) {
  try {
    if (window.crypto?.subtle) {
      const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
      return toHex(new Uint8Array(digest));
    }
  } catch { /* fall through */ }
  return sha256Fallback(str);
}

export async function makeNonce() {
  const r = await axios.post('/auth/social/nonce');
  const raw = r.data?.nonce;
  if (typeof raw !== 'string' || !raw) throw new Error('no nonce');
  return { raw, hashed: await sha256hex(raw) };
}
// Nonces outlive a page that's left open for a while only so long: re-fetch
// a held one after this (the server keeps them 30 minutes).
export const NONCE_REFRESH_MS = 20 * 60 * 1000;

// ── Script loading (web) ────────────────────────────────────────────────────
const scripts = {};
export function loadScript(src) {
  if (!scripts[src]) {
    scripts[src] = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.onload = () => resolve();
      el.onerror = () => { delete scripts[src]; reject(new Error('script failed to load')); };
      document.head.appendChild(el);
    });
  }
  return scripts[src];
}
export const loadAppleJs = () => loadScript(APPLE_JS);
export const loadGoogleGis = () => loadScript(GOOGLE_GIS);

// A sign-in the person backed out of: say nothing.
export class SocialCancelled extends Error {}
const looksCancelled = (e) => /cancel|popup_closed|user_cancelled|1001|closed by user|dismiss/i.test(String(e?.error || e?.message || e?.code || e || ''));

// ── Apple (web) ─────────────────────────────────────────────────────────────
// Must run from the click itself (popup blockers, Safari especially), so the
// script AND the nonce are fetched before the click (components/SocialSignIn
// holds a ready nonce) and nothing awaits between the click and signIn().
export async function appleWebSignIn(cfg, ready) {
  if (!window.AppleID) await loadAppleJs();
  const { raw, hashed } = ready || await makeNonce();
  const state = toHex(window.crypto.getRandomValues(new Uint8Array(16)));
  const redirectURI = `${window.location.origin}/api/auth/apple/callback`;
  window.AppleID.auth.init({
    clientId: cfg.apple.servicesId,
    scope: 'name email',
    redirectURI,
    state,
    nonce: hashed,
    usePopup: true,
  });
  let res;
  try {
    res = await window.AppleID.auth.signIn();
  } catch (e) {
    if (looksCancelled(e)) throw new SocialCancelled();
    throw e;
  }
  const a = res?.authorization || {};
  if (!a.id_token || a.state !== state) throw new Error('Apple sign-in did not complete');
  return {
    provider: 'apple',
    idToken: a.id_token,
    authorizationCode: a.code,
    redirectUri: redirectURI,
    nonce: raw,
    firstName: res.user?.name?.firstName || undefined,
    lastName: res.user?.name?.lastName || undefined,
  };
}

// ── Native (iOS / Android) ──────────────────────────────────────────────────
let nativeReady = null;
async function nativePlugin(cfg) {
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  if (!nativeReady) {
    const ios = runtimePlatform() === 'ios';
    const options = {};
    if (cfg.google?.webClientId) {
      options.google = ios
        ? { iOSClientId: cfg.google.iosClientId, iOSServerClientId: cfg.google.webClientId, mode: 'online' }
        : { webClientId: cfg.google.webClientId, mode: 'online' };
    }
    // iOS only: the native sheet, no redirect; the plugin returns the
    // one-off authorizationCode for the server to exchange (revocation).
    if (ios) options.apple = { clientId: IOS_BUNDLE_ID, redirectUrl: '', useProperTokenExchange: true };
    nativeReady = SocialLogin.initialize(options).catch((e) => { nativeReady = null; throw e; });
  }
  await nativeReady;
  return SocialLogin;
}

export async function nativeSignIn(provider, cfg) {
  const SocialLogin = await nativePlugin(cfg);
  const { raw, hashed } = await makeNonce();
  let res;
  try {
    // No `scopes` for Google: the defaults (openid, email, profile) are all we
    // need, and custom scopes on Android would demand a modified MainActivity.
    res = provider === 'apple'
      ? await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'], nonce: hashed } })
      : await SocialLogin.login({ provider: 'google', options: { nonce: hashed } });
  } catch (e) {
    if (looksCancelled(e)) throw new SocialCancelled();
    throw e;
  }
  const r = res?.result || {};
  if (!r.idToken) throw new Error('Sign-in did not complete');
  const out = { provider, idToken: r.idToken, nonce: raw };
  if (provider === 'apple') {
    out.authorizationCode = r.authorizationCode || undefined;
    out.firstName = r.profile?.givenName || undefined;
    out.lastName = r.profile?.familyName || undefined;
  }
  return out;
}

export { sha256Fallback as _sha256ForTests };
